import { isConnectedAccount } from 'owa-accounts-store';
import { categoryMenuItemMenuText_0 } from './calendarItemCategoryMenuItems.locstring.json';
import loc from 'owa-localize';
import type * as React from 'react';
import type { CalendarEntry, LocalCacheForRemoteCalendarEntry } from 'owa-graph-schema';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';

import {
    CategoryActionSource,
    getCategoryMenuProps,
    getMasterCategoryList,
    lazyUpdateLastTimeUsedMasterCategories,
} from 'owa-categories';
import {
    DirectionalHint,
    IContextualMenuItem,
    IContextualMenuProps,
} from '@fluentui/react/lib/ContextualMenu';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import { lazyMountAndShowFullOptions } from 'owa-options-view';

import styles from './calendarItemCategoryMenuItems.scss';

export const CategoryCommandButtonId = 'CategoryMenuActionButton';

export interface GenericActionButtonProps {
    // this is to avoid a circular dependency of this menu with meeting-forms-common-view
    id: string;
    caption: string;
    icon: string;
    menuItems: IContextualMenuItem[];
}

/**
 * Creates the categorize filterable menu item with the shared control for the given item. Uses the categories for
 * the shared calendar if the item is from a remote calendar and we have read permission- otherwise uses the
 * default categories from the user configuration
 * @param calendarEntry: CalendarEntry, This specifies the type of calendar entry which can be obtained from getCalendarEntryByFolderId
 * @param event: CalendarEvent, the event for which the changes are to be made
 * @param actionSource: CategoryActionSource, This determines whether it is in command bar or in the context menu
 * @param menuItemTitle: string, This specifies the string title of the Categorize menu items
 * @param onCheckedCategoryClickedCallback: (newCategoryList: string[]) => void, This signifies the actions which should be done on the modified category list when a checked category is clicked
 * @param onUncheckedCategoryClickedCallback: (newCategoryList: string[]) => void, This signifies the actions which should be done on the modified category list when an unchecked category is clicked
 * @param onAfterCreateNewCategoryCallback: (newCategoryList: string[]) => void, This signifies the actions which should be done on the modified category list when a new category is clicked
 * @param shouldShowMenu: boolean, This signifies if the button is to be shown in the command bar or surface or not ( for feature flags especially 0)
 * @param shouldShowManageCategories: boolean, This signifies if the manage categories button is to be shown or not
 * @param shouldShowSearchBox: boolean, Should we show the search box in the categories list
 * @param dismissMenu: () => {} function to dismiss the menu
 * @param icon?: string optional identifier for the icon of the category object
 */

export function getCalendarItemCategoryMenuFilterableItem(
    calendarEntry: CalendarEntry,
    event: CalendarEvent,
    actionSource: CategoryActionSource,
    onCheckedCategoryClickedCallback: (newCategoryList: string[]) => void,
    onUncheckedCategoryClickedCallback: (newCategoryList: string[]) => void,
    onAfterCreateNewCategoryCallback: (newCategoryList: string[]) => void,
    shouldShowMenu: boolean,
    shouldShowManageCategories: boolean,
    shouldShowSearchBox: boolean,
    dismissMenu: () => void,
    icon?: string,
    disabled?: boolean,
    onMenuOpened: (ev?: any, item?: IContextualMenuItem) => void = () => {},
    onRender?: (item: any) => React.ReactNode
): FilterableMenuItem {
    return new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: loc(categoryMenuItemMenuText_0),
        [FilterableMenuItemArgs.icon]: icon,
        [FilterableMenuItemArgs.shouldShow]: () => shouldShowMenu,
        [FilterableMenuItemArgs.disabled]: disabled,
        [FilterableMenuItemArgs.menuContent]: getCategoryMenu(
            calendarEntry,
            event,
            actionSource,
            onCheckedCategoryClickedCallback,
            onUncheckedCategoryClickedCallback,
            onAfterCreateNewCategoryCallback,
            shouldShowManageCategories,
            shouldShowSearchBox,
            dismissMenu,
            onMenuOpened
        ),
        [FilterableMenuItemArgs.key]: CategoryCommandButtonId,
        [FilterableMenuItemArgs.onRender]: onRender,
    });
}

/**
 * Creates the categorize menu object friendly for CommandBar with the shared control for the given item. Uses the categories for
 * the shared calendar if the item is from a remote calendar and we have read permission- otherwise uses the
 * default categories from the user configuration
 * @param calendarEntry: CalendarEntry, This specifies the type of calendar entry which can be obtained from getCalendarEntryByFolderId
 * @param event: CalendarEvent, the event for which the changes are to be made
 * @param actionSource: CategoryActionSource, This determines whether it is in command bar or in the context menu
 * @param menuItemTitle: string, This specifies the string title of the Categorize menu items
 * @param onCheckedCategoryClickedCallback: (newCategoryList: string[]) => void, This signifies the actions which should be done on the modified category list when a checked category is clicked
 * @param onUncheckedCategoryClickedCallback: (newCategoryList: string[]) => void, This signifies the actions which should be done on the modified category list when an unchecked category is clicked
 * @param onAfterCreateNewCategoryCallback: (newCategoryList: string[]) => void, This signifies the actions which should be done on the modified category list when a new category is clicked
 * @param shouldShowManageCategories: boolean, This signifies if the manage categories button is to be shown or not
 * @param shouldShowSearchBox: boolean, Should we show the search box in the categories list
 * @param dismissMenu: () => {} function to dismiss the menu
 * @param icon?: string optional identifier for the icon of the category object
 */

export function getCalendarItemCategoryMenuCommandBarActionButtonProps(
    calendarEntry: CalendarEntry,
    event: CalendarEvent,
    actionSource: CategoryActionSource,
    onCheckedCategoryClickedCallback: (newCategoryList: string[]) => void,
    onUncheckedCategoryClickedCallback: (newCategoryList: string[]) => void,
    onAfterCreateNewCategoryCallback: (newCategoryList: string[]) => void,
    shouldShowManageCategories: boolean,
    shouldShowSearchBox: boolean,
    dismissMenu: () => void,
    icon?: string
): GenericActionButtonProps {
    return {
        id: CategoryCommandButtonId,
        icon: icon || null,
        caption: loc(categoryMenuItemMenuText_0),
        menuItems: getCategoryMenu(
            calendarEntry,
            event,
            actionSource,
            onCheckedCategoryClickedCallback,
            onUncheckedCategoryClickedCallback,
            onAfterCreateNewCategoryCallback,
            shouldShowManageCategories,
            shouldShowSearchBox,
            dismissMenu
        ).items,
    };
}

function getCategoryMenu(
    calendarEntry: CalendarEntry,
    event: CalendarEvent,
    actionSource: CategoryActionSource,
    onCheckedCategoryClickedCallback: (newCategoryList: string[]) => void,
    onUncheckedCategoryClickedCallback: (newCategoryList: string[]) => void,
    onAfterCreateNewCategoryCallback: (newCategoryList: string[]) => void,
    shouldShowManageCategories: boolean,
    shouldShowSearchBox: boolean,
    dismissMenu: () => void,
    onMenuOpened: (ev?: any, item?: IContextualMenuItem) => void = () => {}
): IContextualMenuProps {
    // by default, we use our master category list and allow for creating and managing categories
    let manageCategoriesDisabled = false;
    let createNewCategoryDisabled = false;
    const mailboxInfo = event?.ParentFolderId ? event.ParentFolderId.mailboxInfo : undefined;
    let categoryList = getMasterCategoryList(mailboxInfo);

    if (mailboxInfo && isConnectedAccount(mailboxInfo.userIdentity)) {
        createNewCategoryDisabled = true;
    }

    if ((calendarEntry as LocalCacheForRemoteCalendarEntry)?.RemoteCategories) {
        // on remote calendars, we use the remote category list and don't allow managing categories
        manageCategoriesDisabled = true;
        createNewCategoryDisabled = true;
        categoryList = (calendarEntry as LocalCacheForRemoteCalendarEntry).RemoteCategories
            .MasterList;
    }

    const categoryMenuProps = getCategoryMenuProps({
        mailboxInfo: mailboxInfo,
        actionSource: actionSource,
        categoryList: categoryList,
        getCategoriesToCheck: () => {
            return event.Categories ? event.Categories.reverse() : [];
        },
        dismissMenu: dismissMenu ? dismissMenu : () => {},
        shouldShowSearchBox: shouldShowSearchBox,
        shouldShowManageCategories: shouldShowManageCategories,
        getShouldShowClearCategories: () => false,
        onAfterCreateNewCategory: (category: string) => {
            const updatedCategoryList: string[] = event.Categories
                ? [category].concat(event.Categories.slice())
                : [category];

            if (!manageCategoriesDisabled) {
                lazyUpdateLastTimeUsedMasterCategories.importAndExecute([category]);
            }

            onAfterCreateNewCategoryCallback?.(updatedCategoryList);
        },
        onCheckedCategoryClicked: (category: string) => {
            const updatedCategoryList: string[] = event.Categories
                ? event.Categories.filter(existingCategory => existingCategory != category)
                : [];
            onCheckedCategoryClickedCallback?.(updatedCategoryList);
        },
        onUncheckedCategoryClicked: (category: string) => {
            const updatedCategoryList: string[] = event.Categories
                ? [category].concat(event.Categories.slice())
                : [category];

            if (!manageCategoriesDisabled) {
                lazyUpdateLastTimeUsedMasterCategories.importAndExecute([category]);
            }

            onUncheckedCategoryClickedCallback?.(updatedCategoryList);
        },
        manageCategoriesDisabled: manageCategoriesDisabled,
        createNewCategoryDisabled: createNewCategoryDisabled,
        onManageCategoryClicked: () =>
            lazyMountAndShowFullOptions.importAndExecute('general', 'categories'),
        directionalHint:
            actionSource === 'ContextLV'
                ? DirectionalHint.rightTopEdge
                : DirectionalHint.bottomAutoEdge,
    });

    categoryMenuProps.directionalHintFixed = false; // without this when the menu opens at bottom of the page it shrinks it
    categoryMenuProps.className = styles.categoriesMenuContainer;
    /**
     * If the callout max height is not defined, when the "all categories" callout opens, it will overflow down past the bottom of the page without a scrollbar. This is because we're
     * specifying that directionalHintFixed=false, which tells Fabric to assume the callout can be repositioned, but the category menu transforming doesn't force a re-render of the context menu
     * even though the height changes. Providing this value forces the height to apply a scrollbar.
     */
    categoryMenuProps.calloutProps.calloutMaxHeight = 434; // the height of the category page. Without specifying this, when the "all categories"
    if (onMenuOpened) {
        categoryMenuProps.onMenuOpened = onMenuOpened;
    }

    return categoryMenuProps;
}
